import React, { useEffect, useState } from 'react';
import Topbar from '../component/Topbar';
import { useParams } from "react-router-dom";
import axios from 'axios';
import { BiSearchAlt } from "react-icons/bi";
import { PiMicrosoftExcelLogoDuotone } from "react-icons/pi";
import ReactPaginate from 'react-paginate';
import * as XLSX from "sheetjs-style";
import moment from 'moment-timezone';
import { InteractionStatus } from "@azure/msal-browser";
import { useMsal } from "@azure/msal-react";
import { acquireGetAccessToken } from '../utils/GetGrapApi';
import { checkRole, UserRole } from '../callMsGroup';
import { saveRealRole, getEffectiveRole } from '../utils/roleOverride';
import { Buffer } from 'buffer';
import LoadingScreen from '../component/LoadingScreen';
import { useLang, useLabels } from '../hooks/useLabels';

function EventReport() {
    const { inProgress, instance, accounts } = useMsal();
    let { eventnumber }: any = useParams();
    const [token, setToken] = useState("");
    const [photoData, setPhotoData] = useState("");
    const [role, setRole] = useState<UserRole>('owner');
    const [lang] = useLang();
    const { t } = useLabels('report', token, lang);
    const [filter, setFilter] = useState({ keyword: "", status: "", offset: 0, limit: 30 });
    const [eventId, setEventId] = useState("00");
    const [loading, setLoading] = useState(false);
    const [memberLists, setMemberLists] = useState({ memberLists: [], offset: 0, limit: 30, totalPages: 0, total: 0 });
    const [isExporting, setIsExporting] = useState(false);
    const [statusCounts, setStatusCounts] = useState({ registered: 0, unregistered: 0, total: 0 });
    const [authorized, setAuthorized] = useState(false);
    const [profileMail, setProfileMail] = useState("");
    const [eventOwner, setEventOwner] = useState<string | null>(null);

    const auths = {
        headers: {
            "Content-type": "application/json",
            'Authorization': 'Bearer ' + (sessionStorage.getItem("accessToken") || token)
        }
    };

    const convertToG7 = (date: any) => {
        if (!date) return '';
        const m = typeof date === 'string' && date.includes('Z') ? moment(date) : moment.utc(date);
        return m.tz('Asia/Bangkok').format('DD-MM-YYYY HH:mm');
    };

    const loadData = () => {
        setLoading(true);
        axios.get(process.env.REACT_APP_API_END_POINT + '/v1/events/eventactive?eventNumber=' + eventnumber, auths)
            .then(res => {
                setEventOwner(res?.data?.owner ?? '');
                if (res?.data?.id) {
                    loadMember(res.data.id);
                    setEventId(res.data.id);
                } else {
                    setLoading(false);
                }
            })
            .catch(() => setLoading(false));
    };

    const loadMember = (id: number) => {
        axios.get(
            `${process.env.REACT_APP_API_END_POINT}/v1/member/Members?eventId=${id}&keyword=${encodeURIComponent(filter.keyword)}&status=${filter.status}&offset=${filter.offset}&limit=${filter.limit}`,
            auths
        )
            .then(res => {
                setMemberLists(res.data);
                if (!filter.keyword.trim()) loadStatusCounts(id);
                else setStatusCounts({ registered: 0, unregistered: 0, total: 0 });
            })
            .catch(console.error)
            .finally(() => setLoading(false));
    };

    const loadStatusCounts = (id: number) => {
        const base = `${process.env.REACT_APP_API_END_POINT}/v1/member/Members?eventId=${id}&keyword=`;
        Promise.all([
            axios.get(base + '&status=&offset=0&limit=1', auths),
            axios.get(base + '&status=1&offset=0&limit=1', auths),
        ]).then(([allRes, regRes]) => {
            const total = allRes.data?.total ?? 0;
            const registered = regRes.data?.total ?? 0;
            setStatusCounts({ total, registered, unregistered: Math.max(0, total - registered) });
        }).catch(console.error);
    };

    const handleSearch = () => {
        setFilter(old => ({ ...old, offset: 0 }));
        loadData();
    };

    const handleStatusFilter = (status: string) => {
        setFilter(old => ({ ...old, status, offset: 0 }));
    };

    const handlePageClick = (e: any) => {
        const newOffset = (e.selected * filter.limit) % memberLists.total;
        setFilter(old => ({ ...old, offset: newOffset }));
    };

    const onExport = async () => {
        setIsExporting(true);
        try {
            const res = await axios.get(
                `${process.env.REACT_APP_API_END_POINT}/v1/member/Members?eventId=${eventId}&keyword=${encodeURIComponent(filter.keyword)}&status=${filter.status}&offset=0&limit=999999`,
                auths
            );
            const exportData = res.data.memberLists.map((item: any) => ({
                'Employee Id': item.employeeId,
                'Card No': item.cardNo ?? '',
                'Substitute': item.takeRecieved,
                'Item Name': item.rewardName,
                'Name': item.displayName,
                'Department': item.department,
                'Email': item.mail,
                'Building': item.officeLocation,
                'Register Date': convertToG7(item.updatedAt),
                'Status': item.recieved === "1" ? "Registered" : "Not Registered",
            }));
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(exportData), "Report");
            XLSX.writeFile(workbook, `Events_Report_${eventnumber}.xlsx`);
        } catch (err) {
            console.error(err);
        } finally {
            setIsExporting(false);
        }
    };

    useEffect(() => {
        if (!token && inProgress === InteractionStatus.None) {
            acquireGetAccessToken(instance, accounts).then(async (res: any) => {
                if (!res) return;
                setToken(res.accessToken);
                try {
                    const r1 = await axios('https://graph.microsoft.com/v1.0/me/photo/$value', {
                        headers: { Authorization: `Bearer ${res.accessTokenProfile}` },
                        responseType: 'arraybuffer',
                    });
                    setPhotoData('data:image/jpeg;base64, ' + Buffer.from(r1.data, 'binary').toString('base64'));
                } catch (_) {}
                const adminFlag = await checkRole(res.accessTokenProfile);
                saveRealRole(adminFlag);
                setRole(getEffectiveRole(adminFlag));
                if (adminFlag === 'admin' || adminFlag === 'superadmin') {
                    setAuthorized(true);
                } else {
                    // Not admin — must be an owner of this event
                    try {
                        const me = await axios('https://graph.microsoft.com/v1.0/me', {
                            headers: { Authorization: `Bearer ${res.accessTokenProfile}` },
                        });
                        setProfileMail((me.data?.mail || '').toLowerCase().trim());
                    } catch (_) {
                        window.location.href = `${window.location.origin}/accessdenied`;
                    }
                }
            });
        }
    }, [token, inProgress]);

    useEffect(() => { if (token) loadData(); }, [token]);

    // Owner check — non-admin users must be listed as an event owner
    useEffect(() => {
        if (authorized || eventOwner === null || !profileMail) return;
        const owners = eventOwner.split(';').map(s => s.trim().toLowerCase()).filter(Boolean);
        if (owners.includes(profileMail)) setAuthorized(true);
        else window.location.href = `${window.location.origin}/accessdenied`;
    }, [authorized, eventOwner, profileMail]);

    useEffect(() => { if (token) loadData(); }, [filter.offset]);
    useEffect(() => {
        if (token) {
            setFilter(old => ({ ...old, offset: 0 }));
            loadData();
        }
    }, [filter.keyword, filter.status]);

    if (!token || loading || !authorized) return <LoadingScreen />;

    const showRange = memberLists.total > 0
        ? `${filter.offset + 1}–${Math.min(filter.offset + filter.limit, memberLists.total)}`
        : '0';
    const noKeyword = !filter.keyword.trim();

    return (
        <div className="admin-layout">
            <Topbar token={token} photoData={photoData} role={role} />
            <main className="admin-main">
                <div className="dt-container">

                    {/* Header */}
                    <div className="dt-header">
                        <div>
                            <div className="dt-title">{t('section.title', 'Event Report')}</div>
                            <div className="dt-subtitle">{eventnumber}</div>
                        </div>
                        <button className="btn btn-success btn-sm" onClick={onExport} disabled={isExporting}
                            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontWeight: 600 }}>
                            {isExporting
                                ? <><span className="spinner-border spinner-border-sm" /> {t('btn.exporting', 'Exporting...')}</>
                                : <><PiMicrosoftExcelLogoDuotone size={18} /> {t('btn.export', 'Export')}</>
                            }
                        </button>
                    </div>

                    {/* Stats */}
                    {noKeyword && (
                        <div style={{ display: 'flex', gap: 24, padding: '4px 0 12px', fontSize: '0.875rem' }}>
                            <span style={{ color: '#6b7280' }}>
                                {t('stat.total', 'Total')}:&nbsp;<strong style={{ color: '#111' }}>{statusCounts.total}</strong>
                            </span>
                            <span style={{ color: '#6b7280' }}>
                                {t('status.registered', 'Registered')}:&nbsp;<strong style={{ color: '#1f9d57' }}>{statusCounts.registered}</strong>
                            </span>
                            <span style={{ color: '#6b7280' }}>
                                {t('status.not_registered', 'Not Registered')}:&nbsp;<strong style={{ color: '#c5281f' }}>{statusCounts.unregistered}</strong>
                            </span>
                        </div>
                    )}

                    {/* Toolbar */}
                    <div className="dt-toolbar">
                        <div className="dt-search">
                            <BiSearchAlt className="dt-search-icon" />
                            <input
                                className="dt-search-input"
                                placeholder={t('filter.search_placeholder', 'Search by Employee ID or name…')}
                                value={filter.keyword}
                                onChange={e => setFilter(old => ({ ...old, keyword: e.target.value }))}
                                onKeyDown={e => { if (e.key === 'Enter') handleSearch(); }}
                            />
                            {filter.keyword && (
                                <button className="dt-search-clear"
                                    onClick={() => setFilter(old => ({ ...old, keyword: '', offset: 0 }))}>×</button>
                            )}
                        </div>
                        <div className="dt-filter-group">
                            <button
                                className={`dt-filter-btn dt-filter-btn--all ${filter.status === '' ? 'active' : ''}`}
                                onClick={() => handleStatusFilter('')}>
                                {t('filter.all', 'All')}
                            </button>
                            <button
                                className={`dt-filter-btn ${filter.status === '1' ? 'active' : ''}`}
                                onClick={() => handleStatusFilter('1')}>
                                {t('status.registered', 'Registered')}
                            </button>
                            <button
                                className={`dt-filter-btn dt-filter-btn--expired ${filter.status === '0' ? 'active' : ''}`}
                                onClick={() => handleStatusFilter('0')}>
                                {t('status.not_registered', 'Not Registered')}
                            </button>
                        </div>
                    </div>

                    {/* Table */}
                    <div className="dt-table-wrap">
                        <table className="dt-table" style={{ whiteSpace: 'nowrap' }}>
                            <thead>
                                <tr>
                                    <th style={{ width: 110 }}>{t('col.employee_id', 'Employee Id')}</th>
                                    <th style={{ width: 90 }}>{t('col.card_no', 'Card No')}</th>
                                    <th style={{ width: 120 }}>{t('col.substitute', 'Substitute')}</th>
                                    <th>{t('col.employee_name', 'Name')}</th>
                                    <th style={{ width: 160 }}>{t('col.item_name', 'Item')}</th>
                                    <th style={{ width: 130 }}>{t('col.building', 'Building')}</th>
                                    <th style={{ width: 160 }}>{t('col.department', 'Department')}</th>
                                    <th style={{ width: 150 }}>{t('col.register_date', 'Date')}</th>
                                    <th style={{ width: 130 }}>{t('col.status', 'Status')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr>
                                        <td colSpan={9} className="dt-empty">
                                            <span className="spinner-border spinner-border-sm me-2" />
                                            {t('msg.loading', 'Loading...')}
                                        </td>
                                    </tr>
                                ) : memberLists.memberLists.length === 0 ? (
                                    <tr>
                                        <td colSpan={9} className="dt-empty">
                                            <div>{t('empty.title', 'No data found')}</div>
                                            <small>{t('empty.hint', 'Try changing filters or search keyword')}</small>
                                        </td>
                                    </tr>
                                ) : memberLists.memberLists.map((item: any, i: number) => (
                                    <tr key={i}>
                                        <td style={{ fontFamily: 'monospace', fontWeight: 600 }}>{item.employeeId}</td>
                                        <td style={{ fontFamily: 'monospace' }}>
                                            {item.cardNo
                                                ? <span style={{ background: 'rgba(197,40,31,0.08)', color: '#c5281f', borderRadius: 6, padding: '2px 8px', border: '1px solid rgba(197,40,31,0.2)', fontSize: '0.8rem', fontWeight: 600 }}>{item.cardNo}</span>
                                                : <span style={{ color: '#aaa' }}>—</span>
                                            }
                                        </td>
                                        <td style={{ color: '#6b7280', fontSize: '0.85rem' }}>{item.takeRecieved || '—'}</td>
                                        <td>{item.displayName}</td>
                                        <td style={{ fontSize: '0.85rem' }}>{item.rewardName}</td>
                                        <td style={{ color: '#6b7280', fontSize: '0.85rem' }}>{item.officeLocation || '—'}</td>
                                        <td style={{ color: '#6b7280', fontSize: '0.85rem' }}>{item.department || '—'}</td>
                                        <td style={{ fontSize: '0.8rem', color: '#6b7280' }}>{convertToG7(item.updatedAt)}</td>
                                        <td>
                                            {item.recieved === "1"
                                                ? <span className="dt-badge dt-badge--active">{t('status.registered', 'Registered')}</span>
                                                : <span className="dt-badge dt-badge--expired">{t('status.not_registered', 'Not Registered')}</span>
                                            }
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Footer */}
                    <div className="dt-footer">
                        <div className="dt-count">
                            {t('label.showing', 'Showing')} <strong>{showRange}</strong> {t('label.of', 'of')} <strong>{memberLists.total}</strong> {t('label.records', 'records')}
                        </div>
                        {memberLists.totalPages > 1 && (
                            <ReactPaginate
                                previousLabel="‹"
                                nextLabel="›"
                                pageClassName="page-item"
                                pageLinkClassName="page-link"
                                previousClassName="page-item"
                                previousLinkClassName="page-link"
                                nextClassName="page-item"
                                nextLinkClassName="page-link"
                                breakLabel="..."
                                breakClassName="page-item"
                                breakLinkClassName="page-link"
                                pageCount={memberLists.totalPages}
                                marginPagesDisplayed={1}
                                pageRangeDisplayed={4}
                                onPageChange={handlePageClick}
                                containerClassName="pagination mb-0"
                                activeClassName="active"
                                forcePage={Math.floor(filter.offset / filter.limit)}
                            />
                        )}
                    </div>

                </div>
            </main>
        </div>
    );
}

export default EventReport;
